import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { RowStatus } from '../../../common/enums/row-status.enum';
import { AuthorizationPipe } from '../../../common/pipes/authorization.pipe';
import { CacheKey } from '../../../shared/constants/cachekey.constants';
import { UserInfo } from '../../../shared/interface/user-info.interface';
import { CacheService } from '../../../shared/services/cache.service';
import { CommonService, CommonService as SharedCommonService } from '../../../shared/services/common.service';
import { Utils } from '../../../shared/Utils/utils';
import { CommonMessage } from '../../common/common-messages';
import { NotificationType } from "../../enums/notification-types.enum";
import { Status } from '../../enums/ticket-status.enum';
import { IDepartment } from '../../interface/department';
import { EmailConfiguration } from '../../interface/emailConfiguration';
import { DeleteModal } from '../../models/delete-modal';
import { CustomerService } from '../../services/customer.service';
import { DepartmentService } from '../../services/department.service';

@Component({
    selector: 'email-configuration',
    templateUrl: '../../templates/template1/views/department/email-configuration.component.html',
    styleUrls: ['../../templates/template1/themes/default/css/department.less'],
    providers: [AuthorizationPipe]
})
export class EmailConfigurationComponent implements OnInit {
    @Input() departmentId: number;
    emailConfigurationInfo: IDepartment;
    deleteModal: DeleteModal;
    public emailConfigurationForm: FormGroup;
    message: string;
    ticketSubTypes: any[];
    userInfo: UserInfo;
    isSubmitted: boolean = false;
    emailCategories: any;
    ticketPriorities: any[] = [];
    statusTypes: any[] = [];
    emailConfigurationDetails: EmailConfiguration;
    emailAddress: string;
    operatingCountryId: number;
    isEmailCategoryLoading = true;
    isTicketSubTypeLoading = true;
    isTicketStatusLoading = true;
    isTicketPriorityLoading = true;
    isAssignToUserLoading = true;
    users: any[] = [];
    isCreateFlow: boolean;
    titanInternalEmailId: string;
    isDataExist: Boolean;
    actualEmailConfigValues: EmailConfiguration;
    isDisableUpdateButton: Boolean = false;

    constructor(
        private departmentService: DepartmentService,
        private _formbuilder: FormBuilder,
        private sharedCommonService: SharedCommonService,
        private _commonService: CommonService,
        private _translatePipe: TranslateService,
        private _cacheService: CacheService,
        private _customerservice: CustomerService) {
    }

    ngOnInit() {
        if (environment.dataLayer && environment.dataLayer.length > 0) {
            environment.dataLayer[0].pageCategory = 'email-configuration';
        }
        this.buildForm();
        this.userInfo = this._cacheService.get(CacheKey.UserInfo);
        this.deleteModal = new DeleteModal(this.clearForm.bind(this),
            this.doNotRemoveFormData.bind(this), this.doNotRemoveFormData.bind(this));
        this.deleteModal.viewActionButtons = true;
        this.setFocusToTextBox();
        this.getDetailsForEmailConfiguration();
        this.titanInternalEmailId = environment.ttsInternalEmailId
    }

    /**
     * To Get the Email Configuration related details
     */
    getDetailsForEmailConfiguration(): void {
        setTimeout(() => {
            this.getUsersByDepartment();
            this.getEmailCategory();
            this.getTicketPriority();
            this.getTicketStatus();
            this.departmentId ? this.getEmailConfigurationInfo() : null;
        }, 500);
    }

    /**
    * @description
    * Method for to form email configuration form
    */
    buildForm() {
        const emailPattern = '^[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$';
        this.emailConfigurationForm = this._formbuilder.group({
            operatingCountry: ['', Validators.required],
            department: ['', Validators.required],
            email: ['', [Utils.noEmptyString, , Validators.pattern(emailPattern), Validators.required]],
            emailCategory: ['', Validators.required],
            ticketSubType: ['', Validators.required],
            ticketStatus: [''],
            ticketPriority: ['', Validators.required],
            status: [],
            assignToUser: ['']
        });
    }

    /**
    * Method for to set focus on the element
    */
    setFocusToTextBox() {
        setTimeout(function () {
            const autoFocusElement = document.getElementById('autofocus-element');
            if (autoFocusElement) {
                autoFocusElement.focus();
            }
        }, 200);
    }

    /**
     * @description
     * Method for getting email configuration info based on department id.
     * @returns void
     */
    getEmailConfigurationInfo(): void {
        this.departmentService.getDepartmentById(this.departmentId)
            .subscribe(response => {
                if (response) {
                    this.emailConfigurationInfo = response;
                    this.isCreateFlow = (this.emailConfigurationInfo && this.emailConfigurationInfo.emailConfigurationModel) ? false : true;
                    this.emailConfigurationDetails = (this.emailConfigurationInfo && this.emailConfigurationInfo.emailConfigurationModel) ? this.emailConfigurationInfo.emailConfigurationModel : null;
                    this.actualEmailConfigValues = JSON.parse(JSON.stringify(this.emailConfigurationDetails));
                    this.getTicketSubType();
                    if (this.departmentId > 0 && this.emailConfigurationInfo) {
                        this.bindData();
                    }
                }
            }, (error) => {
                this.emailConfigurationInfo = null;
            });
    }

    /**
     * @description
     * Method for to bind the data for form controls
     * @returns void
     */
    bindData(): void {
        this.isDataExist = (!this.isCreateFlow && this.emailConfigurationInfo && this.emailConfigurationInfo.emailConfigurationModel) ? true : false;
        this.emailConfigurationForm.patchValue({
            operatingCountry: this.emailConfigurationInfo.operatingCountryName,
            department: this.emailConfigurationInfo.name,
            email: (this.isDataExist && this.emailConfigurationInfo.emailConfigurationModel.emailId) ? this.emailConfigurationInfo.emailConfigurationModel.emailId : this.emailConfigurationInfo.email,
            emailCategory: (this.isDataExist && this.emailConfigurationInfo.emailConfigurationModel.emailCategoryId) ? this.emailConfigurationInfo.emailConfigurationModel.emailCategoryId : '',
            ticketSubType: (this.isDataExist && this.emailConfigurationInfo.emailConfigurationModel.ticketSubTypeId) ? this.emailConfigurationInfo.emailConfigurationModel.ticketSubTypeId : '',
            ticketPriority: (this.isDataExist && this.emailConfigurationInfo.emailConfigurationModel.ticketPriorityId) ? this.emailConfigurationInfo.emailConfigurationModel.ticketPriorityId : '',
            ticketStatus: (this.isDataExist && this.emailConfigurationInfo.emailConfigurationModel.ticketStatusId) ? this.emailConfigurationInfo.emailConfigurationModel.ticketStatusId : '',
            status: (this.isDataExist && this.emailConfigurationInfo.emailConfigurationModel.rowStatusId) ? ((this.emailConfigurationInfo.emailConfigurationModel.rowStatusId == RowStatus.Active) ? true : false) : true,
            assignToUser: (this.isDataExist && this.emailConfigurationInfo.emailConfigurationModel.assignToUserId) ? this.emailConfigurationInfo.emailConfigurationModel.assignToUserId : '',

        });
        this.emailAddress = (this.isDataExist && this.emailConfigurationInfo.emailConfigurationModel.emailId) ? this.emailConfigurationInfo.emailConfigurationModel.emailId : this.emailConfigurationInfo.email;
        this.operatingCountryId = this.emailConfigurationInfo.operatingCountryId;
        this.getFormControlValues();
        if (this.isCreateFlow) {
            this.emailConfigurationInfo.emailConfigurationModel = {} as EmailConfiguration;
        }
        if(this.emailConfigurationInfo.status && this.emailConfigurationInfo.status == RowStatus.Inactive) {
          this.isDisableUpdateButton = true;
        }
    }

    /**
     * reset form control
     */
    private resetFormControl(formControlName: string) {
        this.emailConfigurationForm.controls[formControlName].setValidators(null);
        this.emailConfigurationForm.controls[formControlName].updateValueAndValidity();
        this.emailConfigurationForm.controls[formControlName].disable();
    }

    /**
     * @description
     * getTickSubType method is used to get the ticket subtypes based on operating country
     * @returns void
     */
    getTicketSubType(): void {
        this.ticketSubTypes = [];
        let operatingCountry = this.departmentId > 0 ? this.emailConfigurationInfo.operatingCountryId : null;
        if (operatingCountry) {
            this.isTicketSubTypeLoading = true;
            this.departmentService.getTicketTypesAndSubTypes(operatingCountry).subscribe(response => {
                if (response) {
                    let subTypes: any = [];
                    response.forEach(element => {
                        if (element && element.subTypes && element.subTypes.length > 0) {
                            element.subTypes.forEach(item => {
                                if (subTypes.indexOf(item.id) < 0) {
                                    this.ticketSubTypes.push(item);
                                    subTypes.push(item.id);
                                }
                            });
                        }
                    });
                    this.isTicketSubTypeLoading = false;
                } else {
                    this.ticketSubTypes = [];
                    this.isTicketSubTypeLoading = false;
                }
            }, (error) => {
                this.ticketSubTypes = [];
                this.isTicketSubTypeLoading = false;
            });
        }
    }

    /**
    * @description
    * alertOnClearingOfData method is used to create alert when clearing the form
    * @returns void
    */
    alertOnClearingOfData(): void {
        this.setFocusToTextBox();
        this.deleteModal.toggle = true;
        this.deleteModal.message = CommonMessage.clearFormData;
    }

    /**
    * @description
    * clearForm method is used to clear the form values
    * @returns void
    */
    clearForm(): void {
        if (this.departmentId > 0) {
            this.isSubmitted = false;
            this.emailConfigurationForm.patchValue({
                operatingCountry: this.emailConfigurationInfo.operatingCountryName,
                department: this.emailConfigurationInfo.name,
                email: (this.isDataExist && this.actualEmailConfigValues.emailId) ? this.actualEmailConfigValues.emailId : this.emailConfigurationInfo.email,
                emailCategory: (this.isDataExist && this.actualEmailConfigValues.emailCategoryId) ? this.actualEmailConfigValues.emailCategoryId : '',
                ticketSubType: (this.isDataExist && this.actualEmailConfigValues.ticketSubTypeId) ? this.actualEmailConfigValues.ticketSubTypeId : '',
                ticketPriority: (this.isDataExist && this.actualEmailConfigValues.ticketPriorityId) ? this.actualEmailConfigValues.ticketPriorityId : '',
                ticketStatus: (this.isDataExist && this.actualEmailConfigValues.ticketStatusId) ? this.actualEmailConfigValues.ticketStatusId : '',
                status: (this.isDataExist && this.actualEmailConfigValues.rowStatusId) ? ((this.actualEmailConfigValues.rowStatusId == RowStatus.Active) ? true : false) : true,
                assignToUser: (this.isDataExist && this.actualEmailConfigValues.assignToUserId) ? this.actualEmailConfigValues.assignToUserId : '',
            });
            this.getFormControlValues();
        }
    }

    doNotRemoveFormData() {
        //data should not be removed...
    }


    /**
    * @description
    * saveEmailConfig method is used for creating the new email configuration
    * @returns void
    */
    saveEmailConfig(): void {
        let emailConfigValues = this.emailConfigurationForm.getRawValue();
        const emailConfigRequestValue: EmailConfiguration = {
            operatingCountryId: (this.emailConfigurationDetails && this.emailConfigurationDetails.operatingCountryId) ? this.emailConfigurationDetails.operatingCountryId : this.operatingCountryId,
            rowStatusId: emailConfigValues.status ? RowStatus.Active : RowStatus.Inactive,
            emailId: this.emailAddress,
            createdBy: this.userInfo.userId,
            departmentId: this.departmentId,
            ticketSubTypeId: this.emailConfigurationInfo.emailConfigurationModel.ticketSubTypeId,
            emailCategoryId: this.emailConfigurationInfo.emailConfigurationModel.emailCategoryId,
            ticketPriorityId: this.emailConfigurationInfo.emailConfigurationModel.ticketPriorityId,
            ticketStatusId: this.emailConfigurationInfo.emailConfigurationModel.ticketStatusId,
            assignToUserId: this.emailConfigurationInfo.emailConfigurationModel.assignToUserId,
            operatingCountryName: this.emailConfigurationInfo.operatingCountryName
        };
        this.departmentService.saveEmailConfig(emailConfigRequestValue)
            .subscribe(response => {
                if (response.success === true) {
                    let created: any = this._translatePipe.get('emailConfiguration.Email Configuration has been created successfully');
                    this.isSubmitted = false;
                    this._commonService.createNotification('', created.value, NotificationType.SUCCESS);
                    this.getEmailConfigurationInfo();
                } else {
                    let failed: any = [];
                    if (response.validations.length > 0) {
                        response.validations.forEach(item => {
                            this.message = item.message;
                            this._commonService.createNotification('', this.message, NotificationType.ERROR);
                        });
                    } else {
                        failed = this._translatePipe.get('emailConfiguration.Your attempt to create Email Configuration is failed');
                        this._commonService.createNotification('', failed.value, NotificationType.ERROR);
                    }
                }
            }, error => (
                this.message = error[0].message,
                this.sharedCommonService.createNotification('', this.message, NotificationType.ERROR)
            ));
    }

    /**
     * @description
     *  updateEmailConfig method is used for updating existing email configuration
     * @returns void
     */
    updateEmailConfig(): void {
        let emailConfigValues = this.emailConfigurationForm.getRawValue();
        const emailConfigRequest: EmailConfiguration = {
            id: (this.emailConfigurationInfo && this.emailConfigurationInfo.emailConfigurationModel && this.emailConfigurationInfo.emailConfigurationModel.id) ? this.emailConfigurationInfo.emailConfigurationModel.id : null,
            operatingCountryId: (this.emailConfigurationDetails && this.emailConfigurationDetails.operatingCountryId) ? this.emailConfigurationDetails.operatingCountryId : this.operatingCountryId,
            rowStatusId: emailConfigValues.status ? RowStatus.Active : RowStatus.Inactive,
            emailId: this.emailAddress,
            updatedBy: this.userInfo.userId,
            departmentId: this.departmentId,
            ticketSubTypeId: this.emailConfigurationInfo.emailConfigurationModel.ticketSubTypeId,
            emailCategoryId: this.emailConfigurationInfo.emailConfigurationModel.emailCategoryId,
            ticketPriorityId: this.emailConfigurationInfo.emailConfigurationModel.ticketPriorityId,
            ticketStatusId: this.emailConfigurationInfo.emailConfigurationModel.ticketStatusId,
            assignToUserId: this.emailConfigurationInfo.emailConfigurationModel.assignToUserId,
            operatingCountryName: this.emailConfigurationInfo.operatingCountryName
        };
        this.departmentService.saveEmailConfig(emailConfigRequest)
            .subscribe(response => {
                if (response.success === true) {
                    let created: any = this._translatePipe.get('emailConfiguration.Email Configuration has been updated successfully');
                    this.isSubmitted = false;
                    this._commonService.createNotification('', created.value, NotificationType.SUCCESS);
                    this.getEmailConfigurationInfo();
                } else {
                    let failed: any = [];
                    if (response.validations.length > 0) {
                        response.validations.forEach(item => {
                            this.message = item.message;
                            this._commonService.createNotification('', this.message, NotificationType.ERROR);
                        });
                    } else {
                        failed = this._translatePipe.get('emailConfiguration.Your attempt to update Email configuration is failed');
                        this._commonService.createNotification('', failed.value, NotificationType.ERROR);
                    }
                }
            }, error => (
                this.message = error.validations[0].message,
                this.sharedCommonService.createNotification('', this.message, NotificationType.ERROR)
            ));
    }

    /**
     * @description
     *  onSubmit method is used to submit the values from the form
     * @returns void
     */
    onSubmit(emailConfigForm: any, valid?: any): void {
        this.isSubmitted = true;
        if (this.emailConfigurationForm.valid) {
            if (this.departmentId > 0 && !this.isCreateFlow) {
                this.updateEmailConfig();
            } else {
                this.saveEmailConfig();
            }
        }

    }

    /**
      * @description
      * getEmailCategory method is used to get email categories from lookup configuration
      * @returns void
      */
    getEmailCategory(): void {
        this.isEmailCategoryLoading = true;
        this._customerservice.getLookupConfigValues('EmailCategory').subscribe(response => {
            if (response && response.length > 0) {
                this.emailCategories = response;
                this.isEmailCategoryLoading = false;
                this.emailConfigurationForm.patchValue({
                    emailCategory: (this.isDataExist && this.emailConfigurationInfo.emailConfigurationModel.emailCategoryId) ? this.emailConfigurationInfo.emailConfigurationModel.emailCategoryId : ''
                });

            } else {
                this.emailCategories = [];
                this.emailConfigurationForm.controls['emailCategory'].setValue('');
                this.isEmailCategoryLoading = false;
            }
        }, (error) => {
            this.isEmailCategoryLoading = false;
            this.emailConfigurationForm.controls['emailCategory'].setValue('');
            this.emailCategories = [];
        });
    }

    /**
     * @description
     * getTicketPriority method is used to get ticket priorities
     * @returns void
     */
    getTicketPriority(): void {
        this.isTicketPriorityLoading = true;
        this._customerservice.getTicketPriorities().subscribe((response) => {
            if (response) {
                this.ticketPriorities = response;
                this.emailConfigurationForm.controls['ticketPriority'].setValue('');
                this.isTicketPriorityLoading = false;
                this.emailConfigurationForm.patchValue({
                    ticketPriority: (this.isDataExist && this.emailConfigurationInfo.emailConfigurationModel.ticketPriorityId) ? this.emailConfigurationInfo.emailConfigurationModel.ticketPriorityId : ''
                });
            } else {
                this.ticketPriorities = [];
                this.emailConfigurationForm.controls['ticketPriority'].setValue('');
                this.isTicketPriorityLoading = false;
            }
        }, (error) => {
            this.isTicketPriorityLoading = false;
            this.emailConfigurationForm.controls['ticketPriority'].setValue('');
            this.ticketPriorities = [];
        });
    }



    /**
     * @description
     *  getTicketStatus method is used to get ticket statuses
     * @returns void
     */

    getTicketStatus(): void {
        this.isTicketStatusLoading = true;
        this._customerservice.getTicketStatus().subscribe((response) => {
            if (response) {
                this.statusTypes = response;
                this.emailConfigurationForm.controls['ticketStatus'].setValue('');
                this.isTicketStatusLoading = false;
                this.emailConfigurationForm.patchValue({
                    ticketStatus: (this.isDataExist && this.emailConfigurationInfo.emailConfigurationModel.ticketStatusId) ? this.emailConfigurationInfo.emailConfigurationModel.ticketStatusId : ''
                });
            } else {
                this.statusTypes = [];
                this.emailConfigurationForm.controls['ticketStatus'].setValue('');
                this.isTicketStatusLoading = false;
            }
        }, (error) => {
            this.isTicketStatusLoading = false;
            this.emailConfigurationForm.controls['ticketStatus'].setValue('');
            this.statusTypes = [];
        });
    }

    /**
     * @description
     *  onEmailCategoryChange method is used to capture the email category value from form when changing
     * @returns void
     */

    onEmailCategoryChange(): void {
        this.emailConfigurationInfo.emailConfigurationModel.emailCategoryId = this.emailConfigurationForm.value.emailCategory;
    }

    /**
     * @description
     *  onTicketSubTypeChange method is used to capture the ticket subtype value from form when changing
     * @returns void
     */
    onTicketSubTypeChange(): void {
        this.emailConfigurationInfo.emailConfigurationModel.ticketSubTypeId = this.emailConfigurationForm.value.ticketSubType;
    }

    /**
     * @description
     *   onTicketStatusChange method is used to capture the ticket status  value from form when changing
     * @returns void
     */
    onTicketStatusChange(): void {
        this.emailConfigurationInfo.emailConfigurationModel.ticketStatusId = this.emailConfigurationForm.value.ticketStatus;
    }

    /**
     * @description
     * onTicketPriorityChange method is used to capture the ticket priority value from form when changing
     * @returns void
     */
    onTicketPriorityChange(): void {
        this.emailConfigurationInfo.emailConfigurationModel.ticketPriorityId = this.emailConfigurationForm.value.ticketPriority;
    }

    /**
    * @description
    *  getUsersByDepartment method is used to get the csp agents based on department
    * @returns void
    */
    getUsersByDepartment(): void {
        this.isAssignToUserLoading = true;
        this.departmentService.searchUsersWithPagination(this.departmentId, null, null, null, null, null, 2147483647, 1).subscribe((response) => {
            if (response && response["content"]) {
                this.users = response["content"];
                this.isAssignToUserLoading = false;
                this.emailConfigurationForm.patchValue({
                    assignToUser: (this.isDataExist && this.emailConfigurationInfo.emailConfigurationModel.assignToUserId) ? this.emailConfigurationInfo.emailConfigurationModel.assignToUserId : ''
                });
            } else {
                this.isAssignToUserLoading = false;
                this.emailConfigurationForm.controls['assignToUser'].setValue('');
                this.users = [];
            }
        }, (error) => {
            this.isAssignToUserLoading = false;
            this.emailConfigurationForm.controls['assignToUser'].setValue('');
            this.users = [];

        });
    }

    /**
    * @description
    *  onAssignToUserChange method is used to capture the assigned user  value from form when changing
    * @returns void
    */
    onAssignToUserChange(): void {
        this.emailConfigurationInfo.emailConfigurationModel.assignToUserId = this.emailConfigurationForm.value.assignToUser;
    }

    /**
    * @description
    * getFormControlValues is used to reset the values of the form controls
    * @returns void
    */
    getFormControlValues(): void {
        this.resetFormControl('operatingCountry');
        this.resetFormControl('department');
        this.resetFormControl('email');
        this.emailConfigurationForm.get("emailCategory").markAsUntouched();
        this.emailConfigurationForm.get("ticketSubType").markAsUntouched();
        this.emailConfigurationForm.get("ticketPriority").markAsUntouched();
        this.emailConfigurationForm.get("ticketStatus").markAsUntouched();
        this.emailConfigurationForm.get("assignToUser").markAsUntouched();
    }

}



// WEBPACK FOOTER //
// ./src/app/customerservice/components/department/email-configuration.component.ts